/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/dialRuleRestrictionsApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API service functions for Dial Rule Restrictions.
────────────────────────────────────────────────────────────────*/

import {
  requestDialRuleRestrictionsApi,
  DIAL_RULE_RESTRICTIONS_ENDPOINT,
} from '../api';
import {
  DialRuleRestriction,
  ListDialRuleRestrictionsParams,
  ListDialRuleRestrictionsResponse,
  CreateDialRuleRestrictionPayload,
  UpdateDialRuleRestrictionPayload,
} from '../types';

const QUICK_LIST_LIMIT = 100;

/**
 * Fetch Dial Rule Restrictions with pagination and filters
 */
export async function fetchDialRuleRestrictions(
  params: ListDialRuleRestrictionsParams,
  t: (key: string) => string
): Promise<ListDialRuleRestrictionsResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const { data: rawData, pagination } = await requestDialRuleRestrictionsApi<DialRuleRestriction[]>(
    `${DIAL_RULE_RESTRICTIONS_ENDPOINT}?${queryParams.toString()}`,
    t('dialRuleRestrictions.messages.loadError')
  );

  return {
    success: true,
    data: Array.isArray(rawData) ? rawData : [],
    pagination: {
      page: pagination?.page ?? params.page,
      limit: pagination?.limit ?? params.limit,
      total: pagination?.total ?? 0,
      totalPages: pagination?.totalPages ?? 0,
    },
  };
}

/**
 * Fetch single Dial Rule Restriction by ID
 */
export async function fetchDialRuleRestrictionById(
  id: string,
  t: (key: string) => string
): Promise<DialRuleRestriction> {
  const { data } = await requestDialRuleRestrictionsApi<DialRuleRestriction>(
    `${DIAL_RULE_RESTRICTIONS_ENDPOINT}/${id}`,
    t('dialRuleRestrictions.messages.loadError')
  );

  if (!data) {
    throw new Error(t('dialRuleRestrictions.messages.loadError'));
  }

  return data;
}

/**
 * Create new Dial Rule Restriction
 */
export async function createDialRuleRestriction(
  payload: CreateDialRuleRestrictionPayload,
  t: (key: string) => string
): Promise<{ success: boolean; data: DialRuleRestriction & { id?: number }; message?: string }> {
  const trimmedPayload = {
    name: payload.name.trim(),
    description: payload.description?.trim() || '',
    enabled: payload.enabled ?? true,
    rules: payload.rules,
  };

  const { data } = await requestDialRuleRestrictionsApi<Partial<DialRuleRestriction> & { id?: number }>(
    DIAL_RULE_RESTRICTIONS_ENDPOINT,
    t('dialRuleRestrictions.messages.createError'),
    {
      method: 'POST',
      body: JSON.stringify(trimmedPayload),
    }
  );

  return {
    success: true,
    data: (data || { ...trimmedPayload, id: data?.id }) as DialRuleRestriction & { id?: number },
  };
}

/**
 * Update Dial Rule Restriction
 */
export async function updateDialRuleRestriction(
  id: string,
  payload: UpdateDialRuleRestrictionPayload,
  t: (key: string) => string
): Promise<{ success: boolean; data: DialRuleRestriction; message?: string }> {
  const trimmedPayload = {
    name: payload.name?.trim(),
    description: payload.description?.trim() || '',
    enabled: payload.enabled ?? true,
    rules: payload.rules || [],
  };

  const { data } = await requestDialRuleRestrictionsApi<DialRuleRestriction>(
    `${DIAL_RULE_RESTRICTIONS_ENDPOINT}/${id}`,
    t('dialRuleRestrictions.messages.updateError'),
    {
      method: 'PUT',
      body: JSON.stringify(trimmedPayload),
    }
  );

  if (!data) {
    throw new Error('Failed to update Dial Rule Restriction - no data returned');
  }

  return {
    success: true,
    data: data,
  };
}

/**
 * Delete Dial Rule Restriction
 */
export async function deleteDialRuleRestriction(
  id: string,
  t: (key: string) => string
): Promise<void> {
  await requestDialRuleRestrictionsApi<null>(
    `${DIAL_RULE_RESTRICTIONS_ENDPOINT}/${id}`,
    t('dialRuleRestrictions.messages.deleteError'),
    { method: 'DELETE' }
  );
}

/**
 * Fetch Dial Rule Restrictions for Quick List
 */
export async function fetchDialRuleRestrictionsForQuickList(
  t: (key: string) => string
): Promise<DialRuleRestriction[]> {
  const params = new URLSearchParams({
    page: '1',
    limit: QUICK_LIST_LIMIT.toString(),
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const { data } = await requestDialRuleRestrictionsApi<DialRuleRestriction[]>(
    `${DIAL_RULE_RESTRICTIONS_ENDPOINT}?${params.toString()}`,
    t('dialRuleRestrictions.messages.loadError')
  );

  return Array.isArray(data) ? data : [];
}

